import { useState, useEffect } from 'react'
import { Check, Copy, Pencil, Trash2, X, FolderInput } from 'lucide-react'
import Modal from './Modal'
import { normalizeImageKitUrl } from '@lic/media-urls'
import {
  type AdminAsset,
  type AssetFolder,
  type AssetUsageResult,
  getAssetUsage,
  updateAsset,
  deleteAssetSafe,
} from '../lib/api'

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const ENTITY_LABELS: Record<string, string> = {
  groups: 'Group',
  members: 'Member',
  news_articles: 'Artikel',
  discography_releases: 'Rilis',
  media_items: 'Media',
  gacha_items: 'Gacha',
  sale_events: 'Event',
}

interface AssetDetailModalProps {
  asset: AdminAsset
  folders: AssetFolder[]
  token: string
  onClose: () => void
  onUpdate: (updated: AdminAsset) => void
  onDelete: (assetId: string) => void
  onMoveRequest: (assetId: string) => void
}

export default function AssetDetailModal({
  asset,
  folders,
  token,
  onClose,
  onUpdate,
  onDelete,
  onMoveRequest,
}: AssetDetailModalProps) {
  const [usage, setUsage] = useState<AssetUsageResult | null>(null)
  const [usageLoading, setUsageLoading] = useState(true)

  const [editingFilename, setEditingFilename] = useState(false)
  const [draftFilename, setDraftFilename] = useState(asset.filename)
  const [editingAlt, setEditingAlt] = useState(false)
  const [draftAlt, setDraftAlt] = useState(asset.alt)

  const [saving, setSaving] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    setUsageLoading(true)
    getAssetUsage(token, asset.id)
      .then((data) => setUsage(data))
      .catch(() => setUsage({ count: 0, references: [] }))
      .finally(() => setUsageLoading(false))
  }, [token, asset.id])

  const handleSaveFilename = async () => {
    const trimmed = draftFilename.trim()
    if (!trimmed || trimmed === asset.filename) { setEditingFilename(false); return }
    setSaving(true)
    try {
      const updated = await updateAsset(token, asset.id, { filename: trimmed })
      onUpdate(updated)
      setEditingFilename(false)
    } finally {
      setSaving(false)
    }
  }

  const handleSaveAlt = async () => {
    const trimmed = draftAlt.trim()
    if (trimmed === asset.alt) { setEditingAlt(false); return }
    setSaving(true)
    try {
      const updated = await updateAsset(token, asset.id, { alt: trimmed })
      onUpdate(updated)
      setEditingAlt(false)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setDeleteError(null)
    const result = await deleteAssetSafe(token, asset.id)
    if (result.ok) {
      onDelete(asset.id)
    } else if (result.inUse) {
      setConfirmingDelete(false)
      setUsageLoading(true)
      getAssetUsage(token, asset.id)
        .then((data) => setUsage(data))
        .finally(() => setUsageLoading(false))
      setDeleteError('Asset ini sekarang digunakan di suatu tempat dan tidak bisa dihapus.')
    } else {
      setDeleteError(result.message)
    }
  }

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(asset.url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const folderName = asset.folder_id
    ? (folders.find((f) => f.id === asset.folder_id)?.name ?? 'Folder tidak diketahui')
    : 'Tanpa Folder'

  const canDelete = !usageLoading && usage !== null && usage.count === 0

  return (
    <Modal title="Detail Asset" onClose={onClose}>
      <div className="flex gap-5">
        {/* Left: preview */}
        <div className="flex-shrink-0 w-52 flex flex-col gap-3">
          <div className="bg-zinc-950 rounded-lg overflow-hidden flex items-center justify-center h-44">
            <img
              src={normalizeImageKitUrl(asset.url)}
              alt={asset.alt || asset.filename}
              className="max-w-full max-h-full object-contain"
            />
          </div>
          <button type="button"
            onClick={handleCopyUrl}
            className="flex items-center justify-center gap-1.5 text-xs text-zinc-400 hover:text-white border border-zinc-700 hover:border-zinc-500 rounded px-3 py-1.5 transition-colors"
          >
            {copied ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
            {copied ? 'Tersalin!' : 'Copy URL'}
          </button>
          <button type="button"
            onClick={() => onMoveRequest(asset.id)}
            className="flex items-center justify-center gap-1.5 text-xs text-zinc-400 hover:text-white border border-zinc-700 hover:border-zinc-500 rounded px-3 py-1.5 transition-colors"
          >
            <FolderInput size={12} />
            Pindah Folder
          </button>
        </div>

        {/* Right: metadata */}
        <div className="flex-1 min-w-0 space-y-3">
          {/* Filename */}
          <div>
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Nama File</span>
            {editingFilename ? (
              <div className="flex items-center gap-1 mt-0.5">
                <input
                  autoFocus
                  value={draftFilename}
                  onChange={(e) => setDraftFilename(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveFilename(); if (e.key === 'Escape') { setEditingFilename(false); setDraftFilename(asset.filename) } }}
                  className="flex-1 bg-zinc-800 text-white text-sm rounded px-2 py-1 border border-zinc-600 focus:outline-none focus:border-yellow-400 min-w-0"
                />
                <button type="button" onClick={handleSaveFilename} disabled={saving} className="text-green-400 hover:text-green-300 flex-shrink-0">
                  <Check size={14} />
                </button>
                <button type="button" onClick={() => { setEditingFilename(false); setDraftFilename(asset.filename) }} className="text-zinc-500 hover:text-white flex-shrink-0">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 mt-0.5 group/field">
                <p className="text-sm text-white truncate flex-1">{asset.filename}</p>
                <button type="button" onClick={() => setEditingFilename(true)} className="opacity-0 group-hover/field:opacity-100 text-zinc-500 hover:text-zinc-300 flex-shrink-0">
                  <Pencil size={12} />
                </button>
              </div>
            )}
          </div>

          {/* Alt text */}
          <div>
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Alt Text</span>
            {editingAlt ? (
              <div className="flex items-center gap-1 mt-0.5">
                <input
                  autoFocus
                  value={draftAlt}
                  onChange={(e) => setDraftAlt(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveAlt(); if (e.key === 'Escape') { setEditingAlt(false); setDraftAlt(asset.alt) } }}
                  placeholder="Deskripsi gambar untuk aksesibilitas"
                  className="flex-1 bg-zinc-800 text-white text-sm rounded px-2 py-1 border border-zinc-600 focus:outline-none focus:border-yellow-400 min-w-0"
                />
                <button type="button" onClick={handleSaveAlt} disabled={saving} className="text-green-400 hover:text-green-300 flex-shrink-0">
                  <Check size={14} />
                </button>
                <button type="button" onClick={() => { setEditingAlt(false); setDraftAlt(asset.alt) }} className="text-zinc-500 hover:text-white flex-shrink-0">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 mt-0.5 group/field">
                <p className="text-sm text-white truncate flex-1">{asset.alt || <span className="text-zinc-500 italic">kosong</span>}</p>
                <button type="button" onClick={() => setEditingAlt(true)} className="opacity-0 group-hover/field:opacity-100 text-zinc-500 hover:text-zinc-300 flex-shrink-0">
                  <Pencil size={12} />
                </button>
              </div>
            )}
          </div>

          {/* Folder */}
          <div>
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Folder</span>
            <p className="text-sm text-white mt-0.5">{folderName}</p>
          </div>

          {/* Size + Type */}
          <div className="flex gap-4">
            <div>
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Ukuran</span>
              <p className="text-sm text-white mt-0.5">{formatSize(asset.size)}</p>
            </div>
            <div>
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Tipe</span>
              <div className="mt-0.5">
                <span className="text-[10px] tracking-wide px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-300 uppercase">
                  {asset.mime_type.split('/')[1]}
                </span>
              </div>
            </div>
          </div>

          {/* Upload date */}
          <div>
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Diunggah</span>
            <p className="text-sm text-white mt-0.5">
              {new Date(asset.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>

          {/* Usage */}
          <div className="border-t border-zinc-800 pt-3">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Penggunaan</span>
            {usageLoading ? (
              <p className="text-xs text-zinc-500 mt-1">Memuat...</p>
            ) : usage && usage.count > 0 ? (
              <div className="mt-1 space-y-1">
                <p className="text-xs text-amber-400">Digunakan di {usage.count} tempat</p>
                <ul className="space-y-1">
                  {usage.references.map((ref, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs text-zinc-300">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-400 uppercase flex-shrink-0">
                        {ENTITY_LABELS[ref.entity_type] ?? ref.entity_type}
                      </span>
                      <span className="truncate">{ref.entity_name}</span>
                      <span className="text-zinc-600 flex-shrink-0">({ref.column})</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-xs text-green-500 mt-1">Tidak digunakan di mana pun</p>
            )}
          </div>

          {/* Delete */}
          <div className="border-t border-zinc-800 pt-3">
            {deleteError && (
              <p className="text-xs text-red-400 mb-2">{deleteError}</p>
            )}
            {confirmingDelete ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-zinc-400">Yakin hapus asset ini?</span>
                <button type="button"
                  onClick={handleDelete}
                  className="text-xs text-white bg-red-600 hover:bg-red-500 px-3 py-1.5 rounded transition-colors"
                >
                  Hapus
                </button>
                <button type="button"
                  onClick={() => setConfirmingDelete(false)}
                  className="text-xs text-zinc-400 hover:text-white transition-colors"
                >
                  Batal
                </button>
              </div>
            ) : (
              <button type="button"
                onClick={() => canDelete && setConfirmingDelete(true)}
                disabled={!canDelete}
                title={!canDelete && usage && usage.count > 0 ? `Asset ini digunakan di ${usage.count} tempat dan tidak bisa dihapus` : undefined}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded transition-colors ${
                  canDelete
                    ? 'text-red-400 border border-red-800 hover:bg-red-900/30 cursor-pointer'
                    : 'text-zinc-600 border border-zinc-800 cursor-not-allowed'
                }`}
              >
                <Trash2 size={12} />
                Hapus Asset
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}
